/**
 * Module Switcher Component
 * App Launcher for switching between platform modules
 * Uses dialog on desktop and drawer on mobile
 * 
 * Design: Swiss Industrial — 4px radius, no decorative icon backgrounds,
 * label-caps for section headers, minimal elevation.
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid, Search } from 'lucide-react';
import { supabase } from '../../integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from '../ui/drawer';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Separator } from '../ui/separator';
import { useIsMobile } from '../../hooks/use-mobile';
import { useModuleAccess } from '../../hooks/useModuleAccess';
import { cn } from '../../lib/utils';
import type { ModuleDefinition } from '../../config/modules';

const RECENTS_KEY = 'module_switcher_recents';
const MAX_RECENTS = 3;

function getRecentIds(): string[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_RECENTS) : [];
  } catch {
    return [];
  }
}

function pushRecent(moduleId: string) {
  const ids = getRecentIds().filter(id => id !== moduleId);
  ids.unshift(moduleId);
  localStorage.setItem(RECENTS_KEY, JSON.stringify(ids.slice(0, MAX_RECENTS)));
}

interface ModuleCardProps {
  module: ModuleDefinition;
  isActive: boolean;
  onClick: () => void;
  compact?: boolean;
}

const ModuleCard: React.FC<ModuleCardProps> = ({ module, isActive, onClick, compact }) => {
  const Icon = module.icon;
  const isDisabled = module.comingSoon;

  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      className={cn(
        'group flex flex-col items-center gap-2 p-3 rounded transition-colors duration-150',
        'border border-transparent',
        compact ? 'min-h-[72px]' : 'min-h-[88px]',
        isActive && 'bg-primary/8 border-primary/20',
        !isActive && !isDisabled && 'hover:bg-muted/60',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        isDisabled && 'opacity-40 cursor-not-allowed'
      )}
    >
      {/* Icon — bare, no decorative background */}
      <div className="relative flex items-center justify-center w-10 h-10">
        <Icon className={cn(
          'h-5 w-5 transition-colors duration-150',
          isActive ? 'text-primary' : 'text-muted-foreground',
          !isActive && !isDisabled && 'group-hover:text-foreground'
        )} />
      </div>

      {/* Label + Description */}
      <div className="text-center space-y-0.5">
        <p className={cn(
          'text-sm font-medium tracking-tight',
          isActive ? 'text-primary' : 'text-foreground',
          isDisabled && 'text-muted-foreground'
        )}>
          {module.name}
        </p>
        
        {!compact && module.description && !module.comingSoon && (
          <p className="text-[11px] text-muted-foreground leading-tight line-clamp-2">
            {module.description}
          </p>
        )}

        {module.comingSoon && (
          <Badge 
            variant="secondary" 
            className="text-[10px] px-1.5 py-0 h-4 font-normal tracking-wide uppercase"
          >
            Soon
          </Badge>
        )}
      </div>
    </button>
  );
};

interface ModuleSwitcherContentProps {
  accessibleModules: ModuleDefinition[];
  comingSoonModules: ModuleDefinition[];
  currentModuleId: string | undefined;
  onModuleSelect: (module: ModuleDefinition) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchRef: React.RefObject<HTMLInputElement | null>;
}

const ModuleSwitcherContent: React.FC<ModuleSwitcherContentProps> = ({
  accessibleModules,
  comingSoonModules,
  currentModuleId,
  onModuleSelect,
  searchQuery,
  onSearchChange,
  searchRef,
}) => {
  const query = searchQuery.toLowerCase().trim();

  const filteredModules = useMemo(() => 
    accessibleModules.filter(m =>
      m.name.toLowerCase().includes(query) || m.description.toLowerCase().includes(query)
    ), [accessibleModules, query]);

  const filteredComingSoon = useMemo(() =>
    comingSoonModules.filter(m =>
      m.name.toLowerCase().includes(query) || m.description.toLowerCase().includes(query)
    ), [comingSoonModules, query]);

  const recentModules = useMemo(() => {
    if (query) return [];
    const ids = getRecentIds();
    return ids
      .map(id => accessibleModules.find(m => m.id === id))
      .filter((m): m is ModuleDefinition => !!m && m.id !== currentModuleId);
  }, [accessibleModules, currentModuleId, query]);

  return (
    <div className="space-y-4">
      {/* Search */}
      <Input
        ref={searchRef}
        placeholder="Search modules..."
        value={searchQuery}
        onChange={e => onSearchChange(e.target.value)}
        leftIcon={<Search className="h-4 w-4" />}
        className="h-9 text-sm border-0 shadow-none bg-muted/50 focus-visible:bg-muted focus-visible:ring-1"
      />

      {/* Recent Section */}
      {recentModules.length > 0 && (
        <div>
          <p className="label-caps mb-2 px-0.5">Recent</p>
          <div className="grid grid-cols-3 gap-1">
            {recentModules.map((module) => (
              <ModuleCard
                key={module.id}
                module={module}
                isActive={false}
                onClick={() => onModuleSelect(module)}
                compact
              />
            ))}
          </div>
        </div>
      )}

      {recentModules.length > 0 && <Separator />}

      {/* All Modules */}
      <div>
        <p className="label-caps mb-2 px-0.5">Modules</p>
        {filteredModules.length > 0 ? (
          <div className="grid grid-cols-3 gap-1">
            {filteredModules.map((module) => (
              <ModuleCard
                key={module.id}
                module={module}
                isActive={module.id === currentModuleId}
                onClick={() => onModuleSelect(module)}
              />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">No modules found</p>
        )}
      </div>

      {/* Coming Soon */}
      {filteredComingSoon.length > 0 && (
        <>
          <Separator />
          <div>
            <p className="label-caps mb-2 px-0.5">Coming Soon</p>
            <div className="grid grid-cols-3 gap-1">
              {filteredComingSoon.map((module) => (
                <ModuleCard
                  key={module.id}
                  module={module}
                  isActive={false}
                  onClick={() => {}}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const ModuleSwitcher: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { accessibleModules, comingSoonModules, currentModule, loading } = useModuleAccess();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) setSearchQuery('');
  }, [open]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => searchRef.current?.focus(), 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const handleModuleSelect = useCallback(async (module: ModuleDefinition) => {
    if (!module.comingSoon && (module.route || module.externalUrl)) {
      pushRecent(module.id);
      if (module.externalUrl) {
        try {
          const target = new URL(module.externalUrl);
          // If the external URL points to the current origin, use SPA navigation
          if (target.origin === window.location.origin) {
            navigate(target.pathname);
          } else {
            // Cross-origin navigation — on localhost different ports don't share storage.
            // Pass session tokens via URL hash so the target app can restore the session.
            const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
            if (isLocalhost) {
              const { data: { session } } = await supabase.auth.getSession();
              if (session) {
                const payload = btoa(JSON.stringify({
                  access_token: session.access_token,
                  refresh_token: session.refresh_token,
                }));
                const sep = module.externalUrl.includes('?') ? '&' : '?';
                window.location.href = module.externalUrl + sep + 'sso_handoff=' + encodeURIComponent(payload);
              } else {
                window.location.href = module.externalUrl;
              }
            } else {
              // Production: cookie-based SSO handles cross-subdomain auth
              window.location.href = module.externalUrl;
            }
          }
        } catch {
          navigate(module.route);
        }
      } else {
        navigate(module.route);
      }
      setOpen(false);
    }
  }, [navigate]);

  if (loading || accessibleModules.length === 0) {
    return null;
  }

  const triggerButton = (
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        'h-9 w-9 transition-colors duration-150',
        'hover:bg-muted',
        open && 'bg-muted'
      )}
      aria-label="App Launcher"
    >
      <LayoutGrid className={cn(
        'h-5 w-5 transition-colors duration-150',
        open ? 'text-foreground' : 'text-muted-foreground'
      )} />
    </Button>
  );

  const contentProps = {
    accessibleModules,
    comingSoonModules,
    currentModuleId: currentModule?.id,
    onModuleSelect: handleModuleSelect,
    searchQuery,
    onSearchChange: setSearchQuery,
    searchRef,
  };

  // Mobile: Use drawer
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>
          {triggerButton}
        </DrawerTrigger>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="pb-2">
            <DrawerTitle className="text-base font-medium">App Launcher</DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-8 overflow-y-auto flex-1 min-h-0">
            <ModuleSwitcherContent {...contentProps} />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop: Use dialog
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {triggerButton}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[560px] p-0 gap-0">
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-border/60">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-base font-medium tracking-tight">
              App Launcher
            </DialogTitle>
            {currentModule && (
              <Badge variant="secondary" className="text-[10px] font-medium">
                {currentModule.name}
              </Badge>
            )}
          </div>
        </DialogHeader>
        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          <ModuleSwitcherContent {...contentProps} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ModuleSwitcher;
